import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

// Le trim evite qu'un espace parasite dans un secret ne produise une URL
// invalide. Vide reste la valeur normale : le client appelle alors « /api » sur
// l'hote qui le sert.
const API_URL = (import.meta.env.VITE_API_URL ?? '').trim();

export class ApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code: string,
        public readonly details?: unknown
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

interface ApiEnvelope<T, M = unknown> {
    success: boolean;
    data?: T;
    /**
     * Ce que la réponse dit d'elle-même plutôt que du contenu : critères
     * appliqués, provenance des données, pagination. Rarement utile, d'où un
     * accès séparé (getWithMeta) qui laisse le cas courant inchangé.
     */
    meta?: M;
    error?: { code: string; message: string; details?: unknown };
}

/**
 * Client HTTP du Hub.
 *
 * Le jeton d'accès est réclamé à MSAL avant chaque appel : MSAL sert un jeton
 * en cache tant qu'il est valide, et le renouvelle silencieusement sinon. Quand
 * le renouvellement silencieux échoue, l'utilisateur est repassé par une
 * demande interactive plutôt que de recevoir une erreur.
 */
class ApiClient {
    private msal: IPublicClientApplication | null = null;
    private account: AccountInfo | null = null;

    configure(msal: IPublicClientApplication, account: AccountInfo | null) {
        this.msal = msal;
        this.account = account;
    }

    private async getToken(): Promise<string> {
        if (!this.msal || !this.account) {
            throw new ApiError('Session absente', 401, 'NO_SESSION');
        }

        try {
            const result = await this.msal.acquireTokenSilent({
                ...apiRequest,
                account: this.account,
            });
            return result.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                const result = await this.msal.acquireTokenPopup(apiRequest);
                return result.accessToken;
            }
            throw error;
        }
    }

    private async requestEnvelope<T, M>(
        path: string,
        init: RequestInit = {}
    ): Promise<ApiEnvelope<T, M>> {
        const token = await this.getToken();

        const response = await fetch(`${API_URL}${path}`, {
            ...init,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...(init.headers ?? {}),
            },
        });

        let payload: ApiEnvelope<T, M> | null = null;
        try {
            payload = (await response.json()) as ApiEnvelope<T, M>;
        } catch {
            // Une réponse sans corps JSON exploitable (502 d'un proxy, par exemple)
            // ne doit pas masquer le code HTTP réel.
        }

        if (!response.ok || payload?.success === false) {
            throw new ApiError(
                payload?.error?.message ?? `Erreur ${response.status}`,
                response.status,
                payload?.error?.code ?? 'HTTP_ERROR',
                payload?.error?.details
            );
        }

        return payload ?? { success: true };
    }

    private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
        const payload = await this.requestEnvelope<T, never>(path, init);
        return payload.data as T;
    }

    get<T>(path: string) {
        return this.request<T>(path, { method: 'GET' });
    }

    /** Comme get, mais rend aussi l'enveloppe `meta` de la réponse. */
    async getWithMeta<T, M>(path: string): Promise<{ data: T; meta: M | undefined }> {
        const payload = await this.requestEnvelope<T, M>(path, { method: 'GET' });
        return { data: payload.data as T, meta: payload.meta };
    }

    post<T>(path: string, body?: unknown) {
        return this.request<T>(path, {
            method: 'POST',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }

    put<T>(path: string, body?: unknown) {
        return this.request<T>(path, {
            method: 'PUT',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }

    patch<T>(path: string, body?: unknown) {
        return this.request<T>(path, {
            method: 'PATCH',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }

    delete<T>(path: string, body?: unknown) {
        return this.request<T>(path, {
            method: 'DELETE',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }
}

export const api = new ApiClient();

/** Construit une chaîne de requête en omettant les valeurs vides. */
export function buildQuery(params: Record<string, string | boolean | null | undefined>): string {
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === '' || value === false) continue;
        search.set(key, String(value));
    }

    const query = search.toString();
    return query ? `?${query}` : '';
}
